"use client";


import { useState } from "react";
import type { ComponentType } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Mail, Phone, Heart, Globe, Settings, Calendar, Users,
  PhoneForwarded, ShieldCheck, PhoneCall, MessageSquare, Video, MessageCircle,
  Wrench, BookOpen, Database, FileText, Youtube, BarChart3, Building2,
  UserCircle, UsersRound, PhoneOutgoing, FileBarChart, MailCheck,
  Clock, Filter, Target, Mic, CalendarCheck, Link2, MapPin, Navigation, Clock3,
  Contact, Briefcase, UserCog, Stethoscope, ClipboardList, Shield, PlayCircle,
  Layers, Grid3x3, HelpCircle, Upload, Type, FolderOpen,
} from "lucide-react";
import { SiWhatsapp, SiFacebook, SiGooglecalendar, SiZapier } from "react-icons/si";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";

interface CardData {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

// ─────────────────────────────────────────────────────────
// AGENT PROFILE SECTION
// ─────────────────────────────────────────────────────────

const profileTabs = [
  {
    id: "profile", label: "Profile", icon: UserCircle,
    cards: [
      { icon: User,   title: "Persona",          description: "Custom agent name, bio, and communication style" },
      { icon: Mail,   title: "Email",             description: "Dedicated email address for your agent" },
      { icon: Phone,  title: "Phone Number",      description: "Local or toll-free number for patient calls" },
      { icon: Heart,  title: "Personality",       description: "Empathy levels, tone, and bedside manner settings" },
      { icon: Globe,  title: "Language Skills",   description: "Multi-language support with auto-translation" },
      { icon: Mic,    title: "Voice & Inflection",description: "Custom voice tone, pacing, and inflection settings" },
    ],
  },
  {
    id: "coworkers", label: "Team Members", icon: UsersRound,
    cards: [
      { icon: ClipboardList, title: "Patient Coordinator", description: "Manages patient intake, scheduling, and follow-ups" },
      { icon: UserCog,       title: "Office Manager",      description: "Oversees daily operations and staff coordination" },
      { icon: Stethoscope,   title: "Providers",           description: "Connects patients directly with their care providers" },
      { icon: Shield,        title: "Administrator",       description: "Practice administration and compliance oversight" },
    ],
  },
  {
    id: "locations", label: "Locations", icon: MapPin,
    cards: [
      { icon: Building2,  title: "Multi-Location Support",   description: "Manage multiple practice locations from one platform" },
      { icon: Navigation, title: "Location Based Routing",   description: "Automatically route patients to their nearest location" },
      { icon: Contact,    title: "Hours & Contact Info",     description: "Location-specific hours, phone numbers, and addresses" },
      { icon: MapPin,     title: "Driving Directions",       description: "Provide patients with directions to your practice" },
      { icon: Clock3,     title: "Local Time Awareness",     description: "Timezone-aware scheduling and communication" },
    ],
  },
];

export function AgentProfileSection() {
  const productName = getMarketingChrome().productName;
  const [activeTab, setActiveTab] = useState("profile");
  const activeContent = profileTabs.find((t) => t.id === activeTab) || profileTabs[0];

  return (
    <section id="identity" data-testid="section-agent-profile" className="relative py-20 sm:py-28">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-bold tracking-tight">
            {productName} <span className="text-primary">Agent Profile</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }} className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Configure your agent&apos;s persona, team members, and locations — the same profile settings you manage under My Profile in the dashboard.
          </motion.p>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-64 flex-shrink-0">
            <div className="flex md:flex-col gap-1">
              {profileTabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-profile-${tab.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all text-left w-full ${
                    activeTab === tab.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}>
                  <tab.icon className="w-4 h-4 flex-shrink-0" /><span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div key={activeTab} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeContent.cards.map((card) => (
                    <div key={card.title} className="rounded-md border border-border bg-card p-5 hover-elevate"
                      data-testid={`card-profile-${card.title.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <card.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div><h3 className="text-base font-semibold mb-1">{card.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">{card.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────
// AGENT RESPONSIBILITIES SECTION
// ─────────────────────────────────────────────────────────

const responsibilitiesTabs = [
  {
    id: "live-chat", label: "Live Chat", icon: MessageSquare, comingSoon: false,
    cards: [
      { icon: MessageSquare, title: "Website Live Chat",              description: "Real-time AI chat on your website to engage visitors and convert them into patients" },
      { icon: UsersRound,    title: "Team Member Take Over",          description: "Any team member can seamlessly step into an active chat conversation at any time" },
      { icon: FileBarChart,  title: "Detailed Conversation Reports",  description: "Full conversation summaries and outcome tracking for every patient chat interaction" },
      { icon: User,          title: "New Patient Assistant",          description: "Welcome and onboard new patients, capture intake info, and book first visits" },
      { icon: Users,         title: "Existing Patient Coordinator",   description: "Support returning patients with follow-ups, recalls, and account questions" },
      { icon: Calendar,      title: "Appointments and Scheduling",    description: "Book, reschedule, and confirm appointments across all channels" },
      { icon: ShieldCheck,   title: "Insurance Verification",         description: "Handle insurance verification, benefits questions, and authorization support" },
      { icon: Briefcase,     title: "Financing Coordinator",          description: "Guide patients through financing options and payment plan enrollment" },
    ],
  },
  {
    id: "phone-calls", label: "Phone Calls", icon: PhoneCall, comingSoon: false,
    cards: [
      { icon: Clock,          title: "24/7 Receptionist",           description: "Always-on AI receptionist available around the clock, including after hours and weekends" },
      { icon: PhoneCall,      title: "Dedicated Phone Number",      description: "A dedicated practice phone number that routes through your AI receptionist" },
      { icon: PhoneForwarded, title: "Intent Based Call Transfer",  description: "Transfer calls to the right team member based on the caller's detected intent and need" },
      { icon: FileBarChart,   title: "Detailed Call Reports",       description: "Full call summaries, transcripts, and outcome tracking for every patient interaction" },
      { icon: UsersRound,     title: "Team Handoff",                description: "Seamlessly transfer conversations to the right team member with full context" },
      { icon: PhoneForwarded, title: "Call Routing",                description: "Intelligently route calls by caller intent, location, and urgency" },
      { icon: Filter,         title: "Call Screening",              description: "Filter spam callers and prioritize high-value patient inquiries" },
      { icon: Calendar,       title: "Appointments and Scheduling", description: "Book, reschedule, and confirm appointments in real time across all channels" },
      { icon: ShieldCheck,    title: "Insurance Verification",      description: "Verify coverage, explain benefits, and coordinate insurance questions" },
      { icon: Briefcase,      title: "Financing Coordinator",       description: "Present payment plans and financing options to patients" },
      { icon: Navigation,     title: "Transportation Coordinator",  description: "Arrange ride assistance and provide directions for patients who need help" },
    ],
  },
  {
    id: "text-messages", label: "Text Messages", icon: MessageCircle, comingSoon: false,
    cards: [
      { icon: MessageCircle, title: "Two-Way SMS",           description: "Send and receive text messages for quick patient questions and follow-ups" },
      { icon: CalendarCheck, title: "Appointment Reminders", description: "Automated SMS reminders to reduce no-shows and confirm upcoming visits" },
      { icon: UsersRound,    title: "Team Handoff",          description: "Escalate complex SMS conversations to your team with full context" },
      { icon: FileBarChart,  title: "Conversation Reports",  description: "Track every text thread with summaries and outcome reporting" },
      { icon: Calendar,      title: "Scheduling via Text",   description: "Let patients book, reschedule, or confirm appointments over SMS" },
    ],
  },
  {
    id: "email", label: "Email", icon: Mail, comingSoon: false,
    cards: [
      { icon: Mail,         title: "Dedicated Agent Email",    description: "A branded email address for patient and practice communications" },
      { icon: MailCheck,    title: "Inbound Email Handling",   description: "Respond to patient emails with accurate, context-aware replies" },
      { icon: UsersRound,   title: "Team Handoff",             description: "Route complex email threads to the right team member when needed" },
      { icon: FileBarChart, title: "Email Conversation Reports", description: "Full summaries and outcome tracking for every email interaction" },
      { icon: Calendar,     title: "Scheduling via Email",     description: "Book and confirm appointments through email conversations" },
    ],
  },
  {
    id: "browser-calls", label: "Browser Calls", icon: PhoneOutgoing, comingSoon: false,
    cards: [
      { icon: PhoneOutgoing, title: "Website Voice Calls",       description: "Let website visitors start a voice conversation directly in your chat widget" },
      { icon: Mic,           title: "Natural Voice Interaction", description: "Real-time voice AI with custom tone, pacing, and inflection settings" },
      { icon: UsersRound,    title: "Team Handoff",              description: "Transfer browser calls to your team with full conversation context" },
      { icon: FileBarChart,  title: "Call Reports",              description: "Transcripts and summaries for every browser-based voice conversation" },
      { icon: Calendar,      title: "Appointments and Scheduling", description: "Book appointments during browser voice calls without leaving your site" },
    ],
  },
  {
    id: "video-calls", label: "Video Calls", icon: Video, comingSoon: false,
    cards: [
      { icon: Video,        title: "Face-to-Face Video Chat", description: "Connect with patients through video for a more personal experience" },
      { icon: UsersRound,   title: "Team Handoff",            description: "Bring a team member into an active video conversation when needed" },
      { icon: FileBarChart, title: "Video Session Reports",   description: "Summaries and outcome tracking for every video interaction" },
      { icon: Calendar,     title: "Virtual Consultations",   description: "Schedule and conduct video consultations directly from your website" },
    ],
  },
  {
    id: "welcome-video", label: "Welcome Video", icon: PlayCircle, comingSoon: false,
    cards: [
      { icon: PlayCircle, title: "Personalized Welcome Video", description: "Greet website visitors with a custom welcome video from your AI agent" },
      { icon: User,       title: "Brand-Aligned Introduction", description: "Introduce your practice, services, and team in your agent's voice" },
      { icon: Globe,      title: "Multi-Language Support",     description: "Deliver welcome videos in the languages your patients speak" },
      { icon: Settings,   title: "Easy Configuration",         description: "Upload or link a video and control when it appears in the widget" },
    ],
  },
  { id: "whatsapp", label: "WhatsApp", icon: SiWhatsapp, comingSoon: true, cards: [] },
  { id: "messenger", label: "Messenger", icon: SiFacebook, comingSoon: true, cards: [] },
];

export function AgentResponsibilitiesSection() {
  const chrome = getMarketingChrome();
  const isSiteGlue = chrome.brandId === "siteglue";
  const [activeTab, setActiveTab] = useState("live-chat");
  const activeContent = responsibilitiesTabs.find((t) => t.id === activeTab) || responsibilitiesTabs[0];
  const sectionTitle = isSiteGlue ? "Communication Channels" : "Agent Responsibilities";
  const sectionBlurb = isSiteGlue
    ? "Every channel your AI employee works — website chat, phone, SMS, email, and more — with a human always available to step in."
    : "Enable the channels your agent handles — live chat, phone, text, email, and more — matching My Responsibilities in Settings.";

  return (
    <section id="responsibilities" data-testid="section-agent-responsibilities" className="relative py-20 sm:py-28">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute inset-0 bg-muted/30" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-bold tracking-tight">
            {chrome.productName}{" "}
            <span className="text-primary">{sectionTitle}</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }} className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            {sectionBlurb}
          </motion.p>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-64 flex-shrink-0">
            <div className="flex md:flex-col gap-1">
              {responsibilitiesTabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-responsibilities-${tab.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all text-left w-full ${
                    activeTab === tab.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}>
                  <tab.icon className="w-4 h-4 flex-shrink-0" /><span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div key={activeTab} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
                {activeContent.comingSoon ? (
                  <div className="rounded-md border border-border bg-card p-12 flex flex-col items-center justify-center text-center min-h-[250px]">
                    <activeContent.icon className="w-12 h-12 text-primary/40 mb-4" />
                    <h3 className="text-2xl font-bold tracking-tight mb-2">Coming Soon</h3>
                    <p className="text-muted-foreground max-w-md">{activeContent.label} support is coming soon to {getMarketingChrome().productName}.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeContent.cards.map((card) => (
                      <div key={card.title} className="rounded-md border border-border bg-card p-5 hover-elevate"
                        data-testid={`card-responsibilities-${card.title.toLowerCase().replace(/\s+/g, "-")}`}>
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <card.icon className="w-5 h-5 text-primary" />
                          </div>
                          <div><h3 className="text-base font-semibold mb-1">{card.title}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">{card.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

export const SkillsSection = AgentResponsibilitiesSection;

// ─────────────────────────────────────────────────────────
// AGENT TOOLS SECTION
// ─────────────────────────────────────────────────────────

const agentToolsTabs = [
  {
    id: "appointment-tools", label: "Appointment Tools", icon: CalendarCheck,
    cards: [
      { icon: CalendarCheck, title: "Juvonno",            description: "Practice management and appointment scheduling for clinics" },
      { icon: CalendarCheck, title: "NexHealth",          description: "Dental and medical appointment booking integration" },
      { icon: SiGooglecalendar, title: "Google Calendar", description: "Calendar sync and availability management" },
      { icon: Calendar,      title: "Calendly",           description: "Self-service scheduling with Calendly event types" },
      { icon: Calendar,      title: "Appointment Types",  description: "Configure services, durations, and provider availability" },
    ],
  },
  {
    id: "mcp-servers", label: "MCP Servers", icon: Layers,
    cards: [
      { icon: Wrench, title: "Open Dental",       description: "Open-source dental software integration" },
      { icon: Wrench, title: "Dentrix",            description: "Dental practice management sync" },
      { icon: Wrench, title: "Dentrix Enterprise", description: "Enterprise-level dental practice management" },
      { icon: Wrench, title: "Eaglesoft",          description: "Patterson dental practice management" },
      { icon: Wrench, title: "PracticeWorks",      description: "Comprehensive dental practice management" },
      { icon: Wrench, title: "OrthoTrac",          description: "Orthodontic practice management system" },
      { icon: Wrench, title: "Dolphin",            description: "Orthodontic imaging and practice management" },
      { icon: Wrench, title: "Dentrix Ascend",     description: "Cloud-based dental practice management" },
      { icon: Wrench, title: "eClinicalWorks",     description: "Cloud-based EHR and practice management" },
      { icon: Wrench, title: "Athena",             description: "Cloud-based healthcare network services" },
      { icon: Wrench, title: "Curve",              description: "Cloud-based dental practice management" },
      { icon: Wrench, title: "Cloud9",             description: "Orthodontic practice management software" },
      { icon: Wrench, title: "Denticon",           description: "Cloud-based dental practice management" },
      { icon: Wrench, title: "Juvonno",            description: "Practice management integration" },
    ],
  },
  {
    id: "integrations", label: "Integrations", icon: Link2,
    cards: [
      { icon: SiZapier,  title: "Zapier",           description: "Connect to 5,000+ apps and workflows" },
      { icon: BarChart3, title: "WhatConverts",     description: "Lead tracking and call attribution" },
      { icon: BarChart3, title: "Google Analytics", description: "Website traffic and conversion tracking" },
      { icon: Phone,     title: "Dedicated Phone",  description: "Local or toll-free number for your AI agent" },
      { icon: Mail,      title: "Agent Email",      description: "Branded email address for patient communications" },
    ],
  },
];

export function AgentToolsSection() {
  const chrome = getMarketingChrome();
  const isSiteGlue = chrome.brandId === "siteglue";
  const [activeTab, setActiveTab] = useState("appointment-tools");
  const activeContent = agentToolsTabs.find((t) => t.id === activeTab) || agentToolsTabs[0];
  const sectionTitle = isSiteGlue ? "AI Skills" : "Agent Tools";
  const sectionBlurb = isSiteGlue
    ? "Scheduling, handoffs, and the tools your AI employee uses to get work done — always with your team in control."
    : "Appointment tools, MCP servers, and integrations — the same My Tools settings you configure in the dashboard.";

  return (
    <section id="tools" data-testid="section-agent-tools" className="relative py-20 sm:py-28">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-bold tracking-tight">
            {chrome.productName} <span className="text-primary">{sectionTitle}</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }} className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            {sectionBlurb}
          </motion.p>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-64 flex-shrink-0">
            <div className="flex md:flex-col gap-1">
              {agentToolsTabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-tools-${tab.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all text-left w-full ${
                    activeTab === tab.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}>
                  <tab.icon className="w-4 h-4 flex-shrink-0" /><span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div key={activeTab} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeContent.cards.map((card) => (
                    <div key={card.title} className="rounded-md border border-border bg-card p-5 hover-elevate"
                      data-testid={`card-tools-${card.title.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <card.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div><h3 className="text-base font-semibold mb-1">{card.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">{card.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

export const ToolsSection = AgentToolsSection;

// ─────────────────────────────────────────────────────────
// INTEGRATIONS SECTION (Product mega — dedicated page)
// ─────────────────────────────────────────────────────────

const integrationsHighlightTabs = [
  agentToolsTabs.find((t) => t.id === "integrations")!,
  agentToolsTabs.find((t) => t.id === "appointment-tools")!,
  agentToolsTabs.find((t) => t.id === "mcp-servers")!,
];

export function AgentIntegrationsSection() {
  const [activeTab, setActiveTab] = useState("integrations");
  const activeContent =
    integrationsHighlightTabs.find((t) => t.id === activeTab) || integrationsHighlightTabs[0];

  return (
    <section id="integrations" data-testid="section-agent-integrations" className="relative py-20 sm:py-28">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute inset-0 bg-muted/30" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-bold tracking-tight"
          >
            {getMarketingChrome().productName} <span className="text-primary">Integrations</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            Connect calendars, EMR/PMS systems, Zoom, analytics, and custom MCP workflows so your agent works inside your stack.
          </motion.p>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-64 flex-shrink-0">
            <div className="flex md:flex-col gap-1">
              {integrationsHighlightTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-integrations-${tab.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all text-left w-full ${
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <tab.icon className="w-4 h-4 flex-shrink-0" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeContent.cards.map((card) => (
                    <div
                      key={card.title}
                      className="rounded-md border border-border bg-card p-5 hover-elevate"
                      data-testid={`card-integrations-${card.title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <card.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold mb-1">{card.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {card.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────
// AGENT TRAINING SECTION
// ─────────────────────────────────────────────────────────

const trainingTabs = [
  {
    id: "basic", label: "Basic", icon: FolderOpen,
    cards: [
      { icon: Layers,   title: "Company Profile", description: "Practice overview, services, policies, and brand voice for your agent" },
      { icon: FileText, title: "ICP",             description: "Ideal customer profile so your agent knows who it serves and how to respond" },
    ],
  },
  {
    id: "advanced", label: "Advanced", icon: Grid3x3,
    cards: [
      { icon: Globe,      title: "Website",  description: "Import and index content from your practice website for accurate answers" },
      { icon: Upload,     title: "Files",    description: "Upload Word, Excel, PowerPoint, and PDF documents to your knowledge base" },
      { icon: Type,       title: "Text",     description: "Add custom text snippets, procedures, and reference material" },
      { icon: HelpCircle, title: "Q&A",      description: "Structured question-and-answer pairs for precise, repeatable responses" },
      { icon: Youtube,    title: "Video",    description: "Extract knowledge from YouTube and uploaded video content" },
      { icon: Mic,        title: "Audio",    description: "Transcribe and index audio recordings into searchable knowledge" },
    ],
  },
  {
    id: "test", label: "Test", icon: MessageSquare,
    cards: [
      { icon: MessageSquare, title: "Knowledge Testing",   description: "Ask test questions and verify your agent answers from trained content" },
      { icon: Database,      title: "RAG Search Preview",  description: "See which knowledge chunks your agent retrieves for each query" },
      { icon: Target,        title: "Accuracy Validation", description: "Identify gaps in training before patients encounter them" },
    ],
  },
];

export function AgentTrainingSection() {
  const chrome = getMarketingChrome();
  const isSiteGlue = chrome.brandId === "siteglue";
  const [activeTab, setActiveTab] = useState("basic");
  const activeContent = trainingTabs.find((t) => t.id === activeTab) || trainingTabs[0];
  const sectionTitle = isSiteGlue ? "Knowledge & Training" : "Agent Training";
  const sectionBlurb = isSiteGlue
    ? "Teach your AI employee your business — company profile, knowledge bases, and test tools your team can review anytime."
    : "Train your agent with company profile, knowledge bases, and test tools — matching My Training in Settings.";

  return (
    <section id="training" data-testid="section-agent-training" className="relative py-20 sm:py-28">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute inset-0 bg-muted/30" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-bold tracking-tight">
            {chrome.productName} <span className="text-primary">{sectionTitle}</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }} className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            {sectionBlurb}
          </motion.p>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-64 flex-shrink-0">
            <div className="flex md:flex-col gap-1">
              {trainingTabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-training-${tab.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all text-left w-full ${
                    activeTab === tab.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}>
                  <tab.icon className="w-4 h-4 flex-shrink-0" /><span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div key={activeTab} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeContent.cards.map((card) => (
                    <div key={card.title} className="rounded-md border border-border bg-card p-5 hover-elevate"
                      data-testid={`card-training-${card.title.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <card.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div><h3 className="text-base font-semibold mb-1">{card.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">{card.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

// Backward-compatible export aliases
export const IdentitySection = AgentProfileSection;

// ─────────────────────────────────────────────────────────
// COMBINED EXPORT (used by HomePage)
// ─────────────────────────────────────────────────────────

export function Capabilities() {
  return (
    <>
      <AgentProfileSection />
      <AgentResponsibilitiesSection />
      <AgentToolsSection />
      <AgentTrainingSection />
    </>
  );
}
